// Service layer for API calls
import { api } from './api';
import { getApiUrl } from './env';
import type {
    Workspace,
    Team,
    Task,
    KanbanBoard,
    MarketplaceReplica,
    HireContract,
    Plan,
    Subscription,
    CheckoutSession,
    PlanType,
    TaskStatus,
} from '../types/api';

// API Base URL for file uploads
const API_BASE_URL = getApiUrl();

// ============================================================================
// Replica Service
// ============================================================================

export interface CreateReplicaData {
    name: string;
    category: string;
    description: string;
    ownerId: string;
    avatarStyle?: string;
    avatarGender?: string;
    avatarAge?: string;
    avatarEthnicity?: string;
    voiceTone?: string;
    voiceSpeed?: number;
    expertise?: string;
    language?: string;
    responseSpeed?: string;
    personalityTraits?: string[];
    personalityDescription?: string;
    deploymentOption?: string;
    guardianAssignment?: string;
    priceAmount?: number;
    pricePeriod?: string;
}

export interface ReplicaResponse {
    id: string;
    name: string;
    category: string;
    description: string;
    status: string;
    avatarStyle: string;
    avatarGender: string;
    avatarAge: string;
    avatarEthnicity: string;
    avatarImageUrl: string | null;
    voiceTone: string;
    voiceSpeed: number;
    expertise: string;
    language: string;
    responseSpeed: string;
    personalityTraits: string[];
    personality: string;
    deploymentOption: string;
    guardianAssignment: string;
    priceAmount: number;
    pricePeriod: string;
    rating: number;
    reviewCount: number;
    isPublished: boolean;
    createdAt: string;
    updatedAt: string;
    publishedAt: string | null;
    voiceSamples: Array<{
        id: string;
        promptIndex: number;
        audioUrl: string;
        transcription: string | null;
    }>;
    mediaAssets: Array<{
        id: string;
        type: string;
        filename: string;
        url: string;
    }>;
}

export const replicaService = {
    async create(data: CreateReplicaData) {
        return api.post<{ success: boolean; replica: ReplicaResponse; message: string }>('/replica', data);
    },

    async get(id: string) {
        return api.get<{ replica: ReplicaResponse }>(`/replica/${id}`);
    },

    async update(id: string, data: Partial<CreateReplicaData>) {
        return api.patch<{ success: boolean; replica: ReplicaResponse; message: string }>(`/replica/${id}`, data);
    },

    async delete(id: string, confirmDeletion: boolean = true) {
        return api.post<{ success: boolean; message: string }>(`/replica/${id}`, { confirmDeletion });
    },

    async deploy(id: string) {
        return api.post<{ success: boolean; replica: ReplicaResponse; message: string }>(`/replica/${id}/deploy`);
    },

    async unpublish(id: string) {
        return api.post<{ success: boolean; replica: ReplicaResponse; message: string }>(`/replica/${id}/unpublish`);
    },

    async getUserReplicas(userId: string) {
        return api.get<{ replicas: ReplicaResponse[] }>(`/replica/user/${userId}`);
    },

    async getMarketplace(filters?: {
        category?: string;
        search?: string;
        minRating?: number;
        maxPrice?: number;
        page?: number;
        limit?: number;
    }) {
        const params: Record<string, string> = {};
        if (filters?.category) params.category = filters.category;
        if (filters?.search) params.search = filters.search;
        if (filters?.minRating) params.minRating = String(filters.minRating);
        if (filters?.maxPrice) params.maxPrice = String(filters.maxPrice);
        if (filters?.page) params.page = String(filters.page);
        if (filters?.limit) params.limit = String(filters.limit);

        return api.get<{
            replicas: ReplicaResponse[];
            pagination: { page: number; limit: number; total: number; totalPages: number };
        }>('/replica/marketplace', params);
    },

    // File upload methods
    async uploadVoiceSample(replicaId: string, audioBlob: Blob, promptIndex: number, transcription?: string) {
        const formData = new FormData();
        formData.append('audio', audioBlob, 'voice-sample.webm');
        formData.append('promptIndex', String(promptIndex));
        if (transcription) {
            formData.append('transcription', transcription);
        }

        const response = await fetch(`${API_BASE_URL}/upload/voice-sample/${replicaId}`, {
            method: 'POST',
            body: formData,
        });
        return response.json();
    },

    async uploadMediaAsset(replicaId: string, file: File, type: 'AVATAR_PHOTO' | 'TRAINING_DOCUMENT' | 'SAMPLE_CONVERSATION') {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', type);

        const response = await fetch(`${API_BASE_URL}/upload/media/${replicaId}`, {
            method: 'POST',
            body: formData,
        });
        return response.json();
    },

    async setupMcpConnector(replicaId: string, toolId: string) {
        return api.post<{ success: boolean; connector: unknown }>('/mcp-catalog/connectors/setup', {
            toolId,
            replicaId,
            scopes: [],
            approvalPolicy: 'guardian',
        });
    },
};

// ============================================================================
// Workspace Service
// ============================================================================

export const workspaceService = {
    async list() {
        return api.get<{ workspaces: Workspace[] }>('/workspaces');
    },

    async get(id: string) {
        return api.get<{ workspace: Workspace }>(`/workspaces/${id}`);
    },

    async create(data: {
        name: string;
        visibility?: 'private' | 'invite_only';
        company?: {
            name: string;
            vatId?: string;
            country?: string;
            address?: string;
        };
    }) {
        return api.post<{ workspace: Workspace }>('/workspaces', data);
    },

    async update(id: string, data: { name?: string }) {
        return api.patch<{ workspace: Workspace }>(`/workspaces/${id}`, data);
    },

    async delete(id: string) {
        return api.delete<{ success: boolean }>(`/workspaces/${id}`);
    },

    async invite(workspaceId: string, data: { email: string; role: 'employer' | 'collaborator' | 'viewer' }) {
        return api.post<{ invitation: unknown }>(`/workspaces/${workspaceId}/invite`, data);
    },

    async listMembers(workspaceId: string) {
        return api.get<{ members: unknown[] }>(`/workspaces/${workspaceId}/members`);
    },

    async removeMember(workspaceId: string, memberId: string) {
        return api.delete<{ success: boolean }>(`/workspaces/${workspaceId}/members/${memberId}`);
    },

    async getStats() {
        return api.get<{
            stats: {
                representationsMade: number;
                valueReturned: number;
                performanceScore: number;
                totalTasks: number;
                completedTasks: number;
                hiredBy: number;
                escalations: number;
                resolvedEscalations: number;
            };
            onboarding: {
                hasReplica: boolean;
                hasTrainingData: boolean;
                hasReviewedGovernance: boolean;
                isDeployed: boolean;
            };
        }>('/workspaces/me/stats');
    },

    async getRoi() {
        return api.get<{
            roi: {
                timeSavedSec: number;
                timeSavedHours: number;
                timeWorkedSec: number;
                moneySavedCents: number;
                moneySavedEur: number;
                replicaCostCents: number;
                roiMultiplier: number;
                tasksCompleted: number;
                workflowRuns: number;
                lastUpdatedAt: string;
            };
        }>('/workspaces/me/roi');
    },
};

// ============================================================================
// Employment Service
// ============================================================================

export const employmentService = {
    async list() {
        return api.get<{
            employments: Array<{
                id: string;
                workspaceId: string;
                workspaceName: string;
                companyName: string | null;
                status: string;
                autonomyLevel: number;
                contractId: string | null;
                contractStatus: string | null;
                teamCount: number;
                assignedAt: string;
            }>;
        }>('/employments');
    },

    async get(id: string) {
        return api.get<{ employment: unknown }>(`/employments/${id}`);
    },
};

// ============================================================================
// Notification Service
// ============================================================================

export const notificationService = {
    async list(options?: { page?: number; limit?: number; unread?: boolean }) {
        const params = new URLSearchParams();
        if (options?.page) params.set('page', String(options.page));
        if (options?.limit) params.set('limit', String(options.limit));
        if (options?.unread) params.set('unread', 'true');
        const qs = params.toString();
        return api.get<{
            notifications: Array<{
                id: string;
                type: string;
                title: string;
                body: string;
                read: boolean;
                actionUrl: string | null;
                metadata: unknown;
                createdAt: string;
            }>;
            unreadCount: number;
            pagination: { page: number; limit: number; total: number; totalPages: number };
        }>(`/notifications${qs ? `?${qs}` : ''}`);
    },

    async markRead(id: string) {
        return api.patch<{ success: boolean }>(`/notifications/${id}/read`, {});
    },

    async markAllRead() {
        return api.post<{ success: boolean; markedRead: number }>('/notifications/read-all', {});
    },
};

// ============================================================================
// Team Service
// ============================================================================

export const teamService = {
    async list(workspaceId: string) {
        return api.get<{ teams: Team[] }>('/teams', { workspaceId });
    },

    async get(id: string) {
        return api.get<{ team: Team }>(`/teams/${id}`);
    },

    async create(data: { workspaceId: string; name: string; description?: string }) {
        return api.post<{ team: Team }>('/teams', data);
    },

    async update(id: string, data: { name?: string; description?: string; status?: 'ACTIVE' | 'ARCHIVED' }) {
        return api.patch<{ team: Team }>(`/teams/${id}`, data);
    },

    async delete(id: string) {
        return api.delete<{ success: boolean }>(`/teams/${id}`);
    },

    async addMember(teamId: string, data: { userId?: string; replicaId?: string; role?: string }) {
        return api.post<{ member: unknown }>(`/teams/${teamId}/members`, data);
    },

    async removeMember(teamId: string, memberId: string) {
        return api.delete<{ success: boolean }>(`/teams/${teamId}/members/${memberId}`);
    },

    // Task management
    async getTasks(teamId: string) {
        return api.get<{ tasks: Task[]; kanban: KanbanBoard }>(`/teams/${teamId}/tasks`);
    },

    async createTask(teamId: string, data: {
        title: string;
        description?: string;
        status?: TaskStatus;
        priority?: number;
        assigneeId?: string;
        dueDate?: string;
    }) {
        return api.post<{ task: Task }>(`/teams/${teamId}/tasks`, data);
    },

    async updateTask(teamId: string, taskId: string, data: Partial<{
        title: string;
        description: string;
        status: TaskStatus;
        priority: number;
        assigneeId: string | null;
        dueDate: string | null;
    }>) {
        return api.patch<{ task: Task }>(`/teams/${teamId}/tasks/${taskId}`, data);
    },

    async deleteTask(teamId: string, taskId: string) {
        return api.delete<{ success: boolean }>(`/teams/${teamId}/tasks/${taskId}`);
    },
};

// ============================================================================
// Hiring Service
// ============================================================================

export const hiringService = {
    async browseMarketplace(filters?: {
        category?: string;
        search?: string;
        minRating?: number;
        maxPrice?: number;
        page?: number;
        limit?: number;
    }) {
        const params: Record<string, string> = {};
        if (filters?.category) params.category = filters.category;
        if (filters?.search) params.search = filters.search;
        if (filters?.minRating) params.minRating = String(filters.minRating);
        if (filters?.maxPrice) params.maxPrice = String(filters.maxPrice);
        if (filters?.page) params.page = String(filters.page);
        if (filters?.limit) params.limit = String(filters.limit);

        return api.get<{
            replicas: MarketplaceReplica[];
            pagination: { page: number; limit: number; total: number; totalPages: number };
        }>('/hiring/marketplace', params);
    },

    async initiateHiring(data: {
        workspaceId: string;
        replicas: Array<{
            replicaId: string;
            teamId?: string;
            role?: string;
        }>;
        planType?: PlanType;
    }) {
        return api.post<{ contract: HireContract; checkoutUrl: string }>('/hiring/initiate', data);
    },

    async listContracts(workspaceId: string) {
        return api.get<{ contracts: HireContract[] }>('/hiring/contracts', { workspaceId });
    },

    async getContract(id: string) {
        return api.get<{ contract: HireContract }>(`/hiring/contracts/${id}`);
    },

    async activateContract(id: string) {
        return api.post<{ contract: HireContract }>(`/hiring/contracts/${id}/activate`);
    },

    async terminateContract(id: string) {
        return api.post<{ contract: HireContract }>(`/hiring/contracts/${id}/terminate`);
    },

    async assignToTeam(contractId: string, data: { replicaId: string; teamId: string; role?: string }) {
        return api.post<{ success: boolean }>(`/hiring/contracts/${contractId}/assign`, data);
    },
};

// ============================================================================
// Billing Service
// ============================================================================

export const billingService = {
    async getPlans() {
        return api.get<{ plans: Plan[] }>('/billing/plans');
    },

    async getSubscription() {
        return api.get<Subscription>('/billing/subscription');
    },

    async createCheckout(data: {
        planType: PlanType;
        billingPeriod: 'monthly' | 'yearly';
        replicaIds?: string[];
        successUrl: string;
        cancelUrl: string;
        vatId?: string;
        companyName?: string;
        country?: string;
    }) {
        return api.post<CheckoutSession>('/billing/create-checkout', data);
    },

    async getPortalUrl() {
        return api.get<{ url: string }>('/billing/portal');
    },
};
